import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";

import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { DataTable } from "@/components/ui/data-table";
import { getTikTokVideoPerformance, type TikTokVideoPerformance } from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

import { TikTokVideoThumb } from "./tiktok-video-thumb";

const columns: ColumnDef<TikTokVideoPerformance>[] = [
  {
    accessorKey: "title",
    header: "Video",
    cell: ({ row }) => (
      <span className="flex min-w-0 items-center gap-2.5">
        <a href={row.original.url} target="_blank" rel="noreferrer" tabIndex={-1} aria-hidden="true">
          <TikTokVideoThumb src={row.original.thumbnailUrl} title={row.original.title} className="h-12 w-7" />
        </a>
        <span className="flex min-w-0 flex-col">
          <a
            href={row.original.url}
            target="_blank"
            rel="noreferrer"
            className="max-w-[13rem] truncate font-medium text-foreground hover:underline"
            title={row.original.title}
          >
            {row.original.title || "Untitled video"}
          </a>
          <span className="max-w-[13rem] truncate text-xs text-muted-foreground">@{row.original.username}</span>
        </span>
      </span>
    ),
  },
  {
    accessorKey: "views",
    header: "Views",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{formatCompactCount(row.original.views)}</span>,
  },
  {
    accessorKey: "gmv",
    header: "GMV",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums font-medium text-foreground">
        {row.original.gmv ? `$${row.original.gmv.amount}` : "—"}
      </span>
    ),
  },
  {
    accessorKey: "gpm",
    header: "GMV/1k views",
    meta: { fit: true },
    cell: ({ row }) => (
      // GPM is the signal: a high GPM on modest views = content that converts
      // above its reach, i.e. a creator worth recruiting or boosting.
      <span className="whitespace-nowrap tabular-nums">
        {row.original.gpm ? `$${row.original.gpm.amount}` : "—"}
      </span>
    ),
  },
  {
    accessorKey: "clickThroughRate",
    header: "CTR",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{row.original.clickThroughRate ?? "—"}</span>,
  },
];

/**
 * The shop's top TikTok videos by GMV for the last 30 days. The GMV-per-1,000
 * -views column separates reach from conversion: the rows where it is high on
 * modest views are the content (and creators) worth doubling down on.
 */
export function TikTokVideosCard() {
  const query = useQuery({
    queryKey: ["tiktok-video-performance", 30],
    queryFn: () => getTikTokVideoPerformance(30),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return (
    <DashboardCard id="dashboard-tiktok-videos-card" title="TikTok content that converts" bodyClassName="px-3 pb-3">
      {query.isError ? (
        <p className="px-2 pb-2 text-sm text-muted-foreground">
          TikTok video analytics could not be loaded. Try again in a few minutes.
        </p>
      ) : (
        <div className="min-h-[14.5rem]">
          <DataTable
            columns={columns}
            data={(query.data?.videos ?? []).slice(0, 5)}
            isLoading={query.isLoading}
            skeletonRows={5}
            getRowId={(row) => row.id}
            emptyText="No shoppable videos with sales in the last 30 days"
            minTableWidth="min-w-0"
          />
          {query.data?.latestAvailableDate && (
            <p className="px-2 pt-2 text-xs text-muted-foreground">
              Last 30 days, ranked by GMV · TikTok data through {query.data.latestAvailableDate}.
            </p>
          )}
        </div>
      )}
    </DashboardCard>
  );
}
